// src/pages/RegisterPage.tsx
// The only entry point for a new tenant organization — creates the
// Organization + its first admin account, then logs them straight in.
import { useState } from 'react';
import { Building2, Zap, Bell, Lock, Mail, type LucideIcon } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { organizationService } from 'services/organizationService';
import { authService } from 'services/authService';
import { setCredentials } from 'store/slices/authSlice';
import FieldError from 'components/ui/FieldError';
import { isValidEmail } from 'utils/validators';

export default function RegisterPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [orgName, setOrgName]     = useState('');
  const [adminName, setAdminName] = useState('');
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  // Set once registration succeeds but the account still needs email
  // verification (REQUIRE_EMAIL_VERIFICATION=true, the default) — swaps the
  // form for a "check your email" panel instead of navigating into the app.
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string | null>(null);
  const [resending, setResending]       = useState(false);
  const [resendStatus, setResendStatus] = useState('');
  const [attempted, setAttempted]       = useState(false);

  const fieldErrors: { orgName?: string; adminName?: string; email?: string; password?: string } = {};
  if (!orgName.trim()) fieldErrors.orgName = 'Organization name is required';
  if (!adminName.trim()) fieldErrors.adminName = 'Your name is required';
  if (!email.trim()) fieldErrors.email = 'Email is required';
  else if (!isValidEmail(email)) fieldErrors.email = 'Enter a valid email address';
  if (password.length < 6) fieldErrors.password = 'Password must be at least 6 characters';
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setAttempted(true);
    if (hasFieldErrors) return;
    setError('');
    setLoading(true);
    try {
      const res = await organizationService.register({ orgName, adminName, email, password });
      if ('emailVerificationRequired' in res) {
        setPendingVerificationEmail(res.email);
      } else {
        dispatch(setCredentials({ user: res.user, accessToken: res.accessToken }));
        navigate('/admin', { replace: true });
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (!pendingVerificationEmail) return;
    setResending(true);
    setResendStatus('');
    try {
      await authService.resendVerification(pendingVerificationEmail);
      setResendStatus('Verification email sent — check your inbox.');
    } catch (err: any) {
      setResendStatus(err?.response?.data?.message || 'Could not resend — try again shortly.');
    } finally {
      setResending(false);
    }
  }

  const inputCls = (hasError?: boolean) =>
    `w-full px-4 py-3.5 bg-[var(--om-card)] border rounded-xl ` +
    `text-[var(--om-text)] text-base outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 ` +
    `placeholder:text-[var(--om-text-dim)] transition-all ${hasError ? 'border-red-500' : 'border-[var(--om-border)]'}`;

  const benefits: { icon: LucideIcon; title: string; desc: string }[] = [
    { icon: Building2, title: 'Your own isolated workspace', desc: 'A dedicated organization space — your data never mixes with any other tenant.' },
    { icon: Zap, title: 'Smart priority engine', desc: 'Tasks are auto-prioritized server-side based on who assigned them to whom.' },
    { icon: Bell, title: 'Real-time notifications', desc: 'Instant updates, email alerts, and a full activity/audit trail on every task.' },
    { icon: Lock, title: 'Secured with JWT', desc: 'Access + refresh token auth, with optional MFA for extra protection.' },
  ];

  return (
    <div className="fixed inset-0 flex flex-col md:flex-row bg-[var(--om-app)] overflow-y-auto">

      {/* Left side — benefits */}
      <div className="hidden md:flex md:w-1/2 flex-col justify-center px-12 lg:px-16 bg-gradient-to-br from-blue-900 via-[#0d1830] to-[var(--om-app)] text-white relative overflow-hidden">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-800 to-blue-500 flex items-center justify-center mb-6 shadow-xl shadow-blue-500/30">
          <Building2 className="w-8 h-8 text-white" strokeWidth={2} />
        </div>
        <h1 className="text-3xl font-black tracking-wide">TODO</h1>
        <p className="text-[13px] text-blue-200/70 mt-1 mb-10">Task Management System</p>

        <div className="space-y-6 max-w-md">
          {benefits.map(b => (
            <div key={b.title} className="flex gap-4">
              <b.icon className="w-6 h-6 shrink-0" strokeWidth={2} />
              <div>
                <p className="font-bold text-sm">{b.title}</p>
                <p className="text-[13px] text-blue-200/60 mt-0.5">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right side — form */}
      <div className="flex-1 flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-[var(--om-panel)] border border-[var(--om-border)] rounded-3xl p-10 lg:p-12 shadow-2xl my-8">

        {/* Brand */}
        <div className="flex items-center justify-start gap-4 mb-8">
          <div className="w-16 h-16 shrink-0 rounded-2xl bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25 ring-4 ring-blue-500/10 animate-bounce [animation-duration:2.5s]">
            <Building2 className="w-8 h-8 text-white" strokeWidth={2} />
          </div>
          <div className="text-left">
            <h1 className="text-2xl font-black text-[var(--om-text)] tracking-tight">Register Your Organization</h1>
            <p className="flex items-center gap-1.5 text-sm text-[var(--om-text-dim)] mt-1">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
              Creates a new, fully isolated workspace with you as its admin
            </p>
          </div>
        </div>

        {pendingVerificationEmail ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-1.5 bg-[var(--om-card)] border border-[var(--om-border)] rounded-xl p-4 text-sm text-[var(--om-text-muted)] text-center">
              <Mail className="w-4 h-4 flex-shrink-0" strokeWidth={2} /> Account created for <strong>{pendingVerificationEmail}</strong>. Check your inbox and click the verification link before signing in.
            </div>

            {resendStatus && (
              <div className="bg-blue-500/10 border border-blue-500/25 rounded-xl px-4 py-3 text-blue-400 text-sm text-center">
                {resendStatus}
              </div>
            )}

            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="inline-flex items-center justify-center gap-2 w-full py-3.5 bg-blue-600 hover:bg-blue-500 disabled:bg-[var(--om-border)] disabled:text-[var(--om-text-dim)] text-white font-bold text-base rounded-xl transition-all"
            >
              {resending ? 'Sending...' : <><Mail className="w-4 h-4" strokeWidth={2} /> Resend Verification Email</>}
            </button>

            <Link
              to="/login"
              className="block w-full text-center text-sm text-blue-400 hover:text-blue-300 font-semibold"
            >
              Go to Login
            </Link>
          </div>
        ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="text-sm text-[var(--om-text-muted)] font-semibold block mb-1.5">Your Name</label>
              <input
                value={adminName}
                onChange={e => setAdminName(e.target.value)}
                placeholder="Full name"
                autoComplete="name"
                className={inputCls(attempted && !!fieldErrors.adminName)}
              />
              <FieldError message={attempted ? fieldErrors.adminName : undefined} />
            </div>

            <div className="flex-1">
              <label className="text-sm text-[var(--om-text-muted)] font-semibold block mb-1.5">Organization Name</label>
              <input
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                placeholder="e.g. Jaipur Municipal Office"
                className={inputCls(attempted && !!fieldErrors.orgName)}
              />
              <FieldError message={attempted ? fieldErrors.orgName : undefined} />
            </div>
          </div>

          <div>
            <label className="text-sm text-[var(--om-text-muted)] font-semibold block mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className={inputCls(attempted && !!fieldErrors.email)}
            />
            <FieldError message={attempted ? fieldErrors.email : undefined} />
          </div>

          <div>
            <label className="text-sm text-[var(--om-text-muted)] font-semibold block mb-1.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Min 6 characters"
              autoComplete="new-password"
              className={inputCls(attempted && !!fieldErrors.password)}
            />
            <FieldError message={attempted ? fieldErrors.password : undefined} />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/25 rounded-xl px-4 py-3 text-red-400 text-sm text-center">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 w-full py-3.5 bg-blue-600 hover:bg-blue-500 disabled:bg-[var(--om-border)] disabled:text-[var(--om-text-dim)] text-white font-bold text-base rounded-xl transition-all shadow-lg shadow-blue-500/20"
          >
            {loading ? 'Creating...' : <><Building2 className="w-4 h-4" strokeWidth={2} /> Create Organization</>}
          </button>
        </form>
        )}

        {!pendingVerificationEmail && (
          <p className="text-center text-sm text-[var(--om-text-dim)] mt-6">
            Already have an account? <Link to="/login" className="text-blue-400 hover:text-blue-300 font-semibold">Sign in</Link>
          </p>
        )}
      </div>
      </div>
    </div>
  );
}
